<template>
  <div class="create-service-form-card">
    <h3 class="card-title">Create New Service</h3>
    <p class="form-instruction">Define the details, pricing, duration, and image for the new offering.</p>

    <form @submit.prevent="submitService" class="service-form-grid">

      <div 
        class="form-group image-dropzone"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        :class="{ 'dragging': isDragging }"
        @click="triggerFileInput"
      >
        <input type="file" ref="fileInput" @change="handleFileChange" style="display: none" accept="image/*">
        <p v-if="!imageFileName">
          <span class="upload-icon">🖼️</span> **Drag & Drop Picture Here or Click to Upload**
        </p>
        <p v-else class="uploaded-filename">
          ✅ File ready: **{{ imageFileName }}**
        </p>
        <small class="upload-note">Mock: Path will be saved as '@/assets/services/service_images/{{ imageFileName }}'</small>
      </div>

      <div class="form-column details-column">
        
        <div class="form-group">
          <label for="service-name">Service Name</label>
          <input type="text" id="service-name" v-model="newService.service_name" required>
        </div>

        <div class="form-group price-group">
          <label for="service-price">Price ($)</label>
          <input type="text" id="service-price" v-model="newService.price" required placeholder="50.00">
        </div>
        
        <div class="form-group duration-group">
            <label for="service-duration">Duration (Minutes)</label>
            <input 
                type="number" 
                id="service-duration" 
                v-model.number="newService.duration_minutes" 
                required 
                min="15" 
                step="5"
                placeholder="60"
            >
            <small class="input-note">Minimum duration is 15 minutes.</small>
        </div>

      </div>

     <div class="form-column description-column">

        <div class="form-group category-group">
              <label for="service-category">Category</label>
              <select id="service-category" v-model="newService.category">
                <option value="" disabled>Select Category</option>
                <option value="Knotless">Knotless</option>
                <option value="Bohemian">Bohemian</option>
                <option value="Twist">Twist</option>
                <option value="others">Others</option>
              </select>
            </div>

        <div class="form-group full-height description-group">
          <label for="service-description">Description</label>
          <textarea id="service-description" v-model="newService.description" rows="7"></textarea>
        </div>
        
      </div>
      
      <div class="action-buttons full-width">
        <button type="button" class="btn-cancel" @click="$emit('cancel')">Cancel</button>
        <button type="submit" class="btn-submit">Save Service</button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
    name: 'CreateService',
    emits: ['create-service', 'cancel'],
    data() {
        return {
            newService: {
                service_name: '',
                description: '', // optional
                price: '',
                // image_path: '', not needed since we would store the entire image
                category: '',
                duration_minutes: null 
            },
            // Image Upload State
            imageFile: null, // holds the actual file object, optional
            imageFileName: '', //optional
            isDragging: false,
        };
    },
    methods: {
        // --- Image Upload Methods (Mock) ---
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.imageFile = file // store the file object
                this.imageFileName = file.name;
                this.isDragging = false;
            }
        },
        handleDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer.files[0];
            if (file && file.type.startsWith('image/')) {
                this.imageFile = file ////
                this.imageFileName = file.name;
            }
        },

        // --- Submission ---
        submitService() {
            if (!this.newService.duration_minutes || this.newService.duration_minutes < 15) {
                alert("Please specify a duration of 15 minutes or more.");
                return;
            }

            // Finalize the mock image path and price format
            //this.newService.image_path = `@/assets/services/service_images/${this.imageFileName}`;
            /*if (!this.newService.price.startsWith('$')) {
                 this.newService.price = `$${parseFloat(this.newService.price).toFixed(2)}`;
            }*/

            //Prepare service data
            const serviceData = {
                ...this.newService,
                imageFile: this.imageFile // pass the file object
            }
            
            // Emit the new service object 
            this.$emit('create-service', serviceData);
        }
    }
}
</script>

<style scoped>

.create-service-form-card {
    background-color: white;
    padding: 35px;
    border-radius: 12px; 
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    border: 2px solid var(--color-gold); /* Gold border for elegance */
    font-family: 'Inter', sans-serif;
}

.card-title {
    color: var(--color-maroon);
    font-size: 2.2rem;
    font-family: 'Playfair Display', serif;
    margin-bottom: 5px;
}

.form-instruction {
    color: #555;
    margin-bottom: 25px;
    padding-bottom: 15px;
    border-bottom: 1px solid var(--color-gold); /* Gold separator */
}

/* Form Layout: Image-First Grid */
.service-form-grid {
    display: grid;
    gap: 25px; 
    grid-template-columns: 1fr; 
}

/* The Image Dropzone */
.image-dropzone {
    grid-column: 1 / -1; 
}

@media (min-width: 800px) {
    .service-form-grid {
        /* */
        grid-template-columns: 1fr 1fr; 
    }
    
    /* Ensure the image and buttons still span both columns */
    .image-dropzone,
    .action-buttons {
        grid-column: span 2; 
    }
}

.form-column {
    display: flex;
    flex-direction: column;
    gap: 20px; 
    padding: 0;
}

.form-group {
    display: flex;
    flex-direction: column;
    width: 100%; 
}

.form-group label {
    font-weight: 700;
    color: var(--color-maroon); /* Maroon for primary labels */
    margin-bottom: 8px;
    font-size: 1.05rem;
}

.form-group input, .form-group select, .form-group textarea {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.3s, box-shadow 0.3s;
}

.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
    border-color: var(--color-earth); /* Earth tone on focus */
    box-shadow: 0 0 0 2px rgba(160, 82, 45, 0.2);
    outline: none;
}

/* Specific Field Adjustments */
.description-group {
    flex-grow: 1;
}
.description-group textarea {
    flex-grow: 1;
    min-height: 150px; /* */
}
.input-note {
    font-size: 0.8em;
    color: #999;
    margin-top: 5px;
    display: block;
}

/* -Image Dropzone Styling*/
.image-dropzone {
    text-align: center;
    border: 4px dashed var(--color-earth); /* Stronger Earth tone border */
    border-radius: 10px;
    padding: 40px 20px;
    cursor: pointer;
    transition: background-color 0.3s, border-color 0.3s;
    margin-bottom: 25px; /* Separate from fields below */
}

.image-dropzone:hover, .image-dropzone.dragging {
    background-color: #fffaf0; /* Light gold background on hover/drag */
    border-color: var(--color-maroon);
}

.upload-icon {
    display: block;
    font-size: 3rem; /* Larger icon */
    margin-bottom: 10px;
}

.uploaded-filename {
    color: var(--color-maroon);
    font-weight: 700;
    font-size: 1.1rem;
}

.upload-note {
    color: #777;
    margin-top: 10px;
}


/* Action Buttons */
.action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 20px;
    padding-top: 25px;
    border-top: 1px solid #eee;
}

.btn-cancel, .btn-submit {
    padding: 12px 25px;
    border: none;
    border-radius: 25px; 
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s, transform 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-cancel {
    background-color: #999; 
    color: white;
}

.btn-cancel:hover {
    background-color: #777;
    transform: translateY(-1px);
}

.btn-submit {
    background-color: var(--color-maroon); /* Primary maroon for submission */
    color: white;
}

.btn-submit:hover {
    background-color: #4a0c19;
    transform: translateY(-1px);
}
</style>